import { useState, useEffect } from 'react';
import { usePark } from '../contexts/ParkContext';
import axios from 'axios';
import { 
  DollarSign, 
  TrendingUp, 
  TrendingDown, 
  AlertTriangle,
  Calculator,
  Building2,
  ArrowRight,
  Plus
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Dashboard = () => {
  const { selectedPark, selectedYear, parks, setSelectedPark } = usePark();
  const [summary, setSummary] = useState(null);
  const [incomeReport, setIncomeReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (selectedPark) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [selectedPark, selectedYear]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [summaryRes, incomeRes] = await Promise.all([
        axios.get(`${API}/dashboard/summary?park_id=${selectedPark.id}&año=${selectedYear}`),
        axios.get(`${API}/reports/income-vs-expenses?park_id=${selectedPark.id}&año=${selectedYear}`)
      ]);
      setSummary(summaryRes.data);
      setIncomeReport(incomeRes.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  const monthNames = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

  const chartData = incomeReport?.data?.map((item, index) => ({
    mes: monthNames[index],
    Ingresos: item.ingresos_cobrados,
    Gastos: item.gastos
  })) || [];

  // Show setup screen if no parks
  if (!loading && parks.length === 0) {
    return (
      <div className="animate-fadeIn">
        <div className="page-header">
          <h1>Dashboard</h1>
          <p>Bienvenido al sistema de gestión de parques industriales</p>
        </div>

        <div className="empty-state py-24">
          <div className="empty-state-icon bg-amber-100">
            <Building2 size={32} className="text-amber-600" />
          </div>
          <h3>Configura tu primer parque</h3>
          <p className="mb-6">Para comenzar, necesitas crear un parque industrial y configurar sus lotes.</p>
          <Button 
            className="btn-industrial bg-slate-900 text-white hover:bg-slate-800"
            onClick={() => navigate('/lotes')}
            data-testid="create-park-btn"
          >
            <Plus size={16} className="mr-2" />
            Crear Parque Industrial
          </Button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="animate-fadeIn">
        <div className="page-header">
          <h1>Dashboard</h1>
          <p>Cargando datos...</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="card-industrial animate-pulse">
              <div className="h-4 bg-slate-200 rounded w-1/2 mb-4"></div>
              <div className="h-8 bg-slate-200 rounded w-3/4"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fadeIn">
      <div className="page-header">
        <h1 data-testid="dashboard-title">Dashboard</h1>
        <p>{selectedPark?.nombre} - Año {selectedYear}</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="stat-card" data-testid="stat-ingresos">
          <div className="flex items-start justify-between mb-4">
            <span className="stat-label">Ingresos Cobrados</span>
            <div className="w-10 h-10 bg-emerald-100 flex items-center justify-center">
              <TrendingUp size={20} className="text-emerald-600" />
            </div>
          </div>
          <span className="stat-value tabular-nums">{formatCurrency(summary?.total_cobrado)}</span>
          <p className="stat-change positive">
            {summary?.porcentaje_cobro?.toFixed(1)}% de cuotas cobradas
          </p>
        </div>

        <div className="stat-card" data-testid="stat-gastos">
          <div className="flex items-start justify-between mb-4">
            <span className="stat-label">Gastos Totales</span>
            <div className="w-10 h-10 bg-red-100 flex items-center justify-center">
              <TrendingDown size={20} className="text-red-600" />
            </div>
          </div>
          <span className="stat-value tabular-nums">{formatCurrency(summary?.total_gastos)}</span>
          <p className="stat-change negative">
            {summary?.porcentaje_gasto?.toFixed(1)}% del presupuesto
          </p>
        </div>

        <div className="stat-card" data-testid="stat-cartera">
          <div className="flex items-start justify-between mb-4">
            <span className="stat-label">Cartera Vencida</span>
            <div className="w-10 h-10 bg-amber-100 flex items-center justify-center">
              <AlertTriangle size={20} className="text-amber-600" />
            </div>
          </div>
          <span className="stat-value tabular-nums">{formatCurrency(summary?.cartera_vencida)}</span>
          <p className="text-sm text-slate-500 mt-2">Pendiente de cobro</p>
        </div>

        <div className="stat-card" data-testid="stat-presupuesto">
          <div className="flex items-start justify-between mb-4">
            <span className="stat-label">Presupuesto Anual</span>
            <div className="w-10 h-10 bg-slate-100 flex items-center justify-center">
              <Calculator size={20} className="text-slate-600" />
            </div>
          </div>
          <span className="stat-value tabular-nums">{formatCurrency(summary?.presupuesto_anual)}</span>
          <p className="text-sm text-slate-500 mt-2">{summary?.total_lotes} lotes activos</p>
        </div>
      </div>

      {/* Chart */}
      <div className="card-industrial mb-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="font-bold text-lg text-slate-900">Ingresos vs Gastos</h3>
            <p className="text-sm text-slate-500">Comparativa mensual del año {selectedYear}</p>
          </div>
          <Button
            variant="outline"
            className="rounded-none"
            onClick={() => navigate('/reportes')}
            data-testid="view-reports-btn"
          >
            Ver Reportes
            <ArrowRight size={16} className="ml-2" />
          </Button>
        </div>

        {chartData.length > 0 ? (
          <div className="h-80" data-testid="income-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis 
                  dataKey="mes" 
                  tick={{ fill: '#64748B', fontSize: 12 }}
                  axisLine={{ stroke: '#E2E8F0' }}
                />
                <YAxis 
                  tick={{ fill: '#64748B', fontSize: 12 }}
                  axisLine={{ stroke: '#E2E8F0' }}
                  tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                />
                <Tooltip 
                  formatter={(value) => formatCurrency(value)}
                  contentStyle={{ 
                    backgroundColor: '#FFF', 
                    border: '1px solid #E2E8F0',
                    borderRadius: '2px'
                  }}
                />
                <Legend />
                <Bar dataKey="Ingresos" fill="#0F172A" />
                <Bar dataKey="Gastos" fill="#F59E0B" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="empty-state py-12">
            <p className="text-slate-500">No hay datos para mostrar. Genera las cuotas del año primero.</p>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <button
          onClick={() => navigate('/cuotas')}
          className="card-industrial text-left hover:border-slate-300 transition-colors group"
          data-testid="quick-cuotas"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-emerald-100 flex items-center justify-center group-hover:bg-emerald-200 transition-colors">
              <DollarSign size={24} className="text-emerald-600" />
            </div>
            <div>
              <h4 className="font-bold text-slate-900">Registrar Pago</h4>
              <p className="text-sm text-slate-500">Capturar ingreso recibido</p>
            </div>
          </div>
        </button>

        <button
          onClick={() => navigate('/gastos')}
          className="card-industrial text-left hover:border-slate-300 transition-colors group"
          data-testid="quick-gastos"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-red-100 flex items-center justify-center group-hover:bg-red-200 transition-colors">
              <TrendingDown size={24} className="text-red-600" />
            </div>
            <div>
              <h4 className="font-bold text-slate-900">Registrar Gasto</h4>
              <p className="text-sm text-slate-500">Capturar egreso</p>
            </div>
          </div>
        </button>

        <button
          onClick={() => navigate('/reportes')}
          className="card-industrial text-left hover:border-slate-300 transition-colors group"
          data-testid="quick-reportes"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-slate-100 flex items-center justify-center group-hover:bg-slate-200 transition-colors">
              <Calculator size={24} className="text-slate-600" />
            </div>
            <div>
              <h4 className="font-bold text-slate-900">Ver Reportes</h4>
              <p className="text-sm text-slate-500">Análisis detallado</p>
            </div>
          </div>
        </button>
      </div>
    </div>
  );
};

export default Dashboard;
